import { useRef } from "react";
import { feedBecause, feedName, formatChange, formatUsd, marketPhrase } from "../../format";
import { lineClass, lineStyle } from "../../line-style";
import type { SetupProps } from "../types";
import { type BannerModel, renderBanner } from "./banner";
import { useBannerCols } from "./use-banner-cols";
// A setup's chrome ships with the setup. `styles.css` holds what every look
// shares; a rule that only one look uses lives in that look's directory, which
// is what makes "a third setup touches registry.ts and its own directory" true
// of a setup that does not look like the other two.
import "./ubuntu.css";

/**
 * An Ubuntu terminal window.
 *
 * Aubergine title bar with the three window buttons, a fixed-width status
 * banner pinned beneath it, and `user@atc:~/paper$` at the prompt. The banner
 * is characters rather than markup — see `banner.ts` for why that matters.
 *
 * Everything on screen came from the view model. This file formats and lays
 * out; it does not fetch and it does not compute. The second setup existing at
 * all is what proves the seam holds, which is the actual deliverable of M3/I2.
 */

const HOME = "~/paper";

/** `amir@example.com` → `amir`, and `guest` when signed out. Cosmetic only. */
function shellUser(email: string | undefined): string {
  if (!email) return "guest";
  const local = email.split("@")[0] ?? "guest";
  return local.replace(/[^a-zA-Z0-9._-]/g, "") || "guest";
}

/**
 * The banner's contents, assembled from what the model already holds.
 *
 * The mock in the iteration showed `market open 14:32` here, and it was
 * deliberately left out of M3/I2 because whether the market was open was not
 * something the API exposed — a banner asserting it would have been the
 * terminal claiming to know something the server had not said. M4 made the
 * server say it, and M4/I6 puts it on the banner: every word of it, including
 * the timezone it is rendered in, comes off `/api/v1/markets`.
 */
function toBanner(model: SetupProps["model"]): BannerModel {
  const { snapshot, marked, session, link, quotes, markets, feed, liveVenue } = model;

  const stats =
    snapshot && marked
      ? [
          { label: "account", value: session.accountName ?? "account" },
          ...(liveVenue ? [{ label: "LIVE", value: liveVenue.accountNumber ?? "alpaca" }] : []),
          { label: "equity", value: formatUsd(marked.equity) },
          { label: "cash", value: formatUsd(snapshot.cash) },
          ...(marked.marketValue !== "0.0000"
            ? [{ label: "holdings", value: formatUsd(marked.marketValue) }]
            : []),
          ...(snapshot.reserved_cash !== "0.0000"
            ? [{ label: "reserved", value: formatUsd(snapshot.reserved_cash) }]
            : []),
        ]
      : [{ label: "", value: session.email ? "no account yet" : "not signed in" }];

  // How far behind the prices are, on the row that already wraps. The number is
  // the server's own — the same policy that decides whether a resting order may
  // trigger against a price — so it cannot promise something the feed is not
  // doing. Omitted rather than shown as `0m` when there is no delay to report.
  const delayMinutes = markets[0]?.quote_delay_minutes ?? 0;
  // `ADR-0039`: real time is said too, not left as the absence of a delay.
  const withDelay =
    markets[0]?.realtime === true
      ? [...stats, { label: "prices", value: "real-time" }]
      : delayMinutes > 0
        ? [...stats, { label: "prices", value: `${delayMinutes}m delayed` }]
        : stats;

  // **Whose feed, beside how late it is.** Both are facts about the prices
  // rather than about the money, which is why they sit together at the end of
  // the row. Until M4/E53 the header said `US` when a trader's own key was
  // working and `SIM` when it had stopped, and left that mapping to be
  // inferred — so this says it. Omitted rather than guessed when there is no
  // answer: `/api/v1/feeds` is behind a session, so a guest has none.
  const withFeed = feed
    ? [...withDelay, { label: "feed", value: `${feedName(feed)} · ${feedBecause(feed.because)}` }]
    : withDelay;

  return {
    stats: withFeed,
    right: session.userId ? `● ${link}` : "guest",
    quotes: quotes.map((q) => ({
      symbol: q.symbol,
      last: formatUsd(q.last),
      direction: q.direction,
      open: q.open,
      change: formatChange(q.changePercent),
    })),
    quiet: link === "live" ? "waiting for the first tick…" : "connecting to market data…",
    // Several venues would crowd a row whose job is prices; the phrase is
    // per-venue and `row` drops it wholesale rather than truncating it, so a
    // deployment serving two markets shows both or neither, never half of one.
    market: markets.map((m) => `${m.id} ${marketPhrase(m)}`).join("  ·  "),
  };
}

export function UbuntuChrome({ model }: SetupProps) {
  const { lines, busy, session } = model;
  const user = shellUser(session.email);
  const title = `${user}@atc: ${HOME}`;
  const prompt = `${user}@atc:${HOME}$`;

  // The banner spans the terminal, so its column count is a measurement rather
  // than a constant. Both refs are needed: the pane says how much room there is,
  // the <pre> says how wide one character of the banner's own font is.
  const bannerPaneRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLPreElement>(null);
  const cols = useBannerCols(bannerPaneRef, bannerRef);
  const banner = renderBanner(cols, toBanner(model));

  return (
    <div className="term ubuntu">
      {/*
        The window buttons are decoration, not controls — there is no window to
        close. They are spans rather than buttons so a screen reader is not
        offered three actions that do nothing.
      */}
      <div className="ubuntu-bar">
        <span className="ubuntu-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span className="ubuntu-title">{title}</span>
      </div>

      <div className="ubuntu-banner-pane" ref={bannerPaneRef}>
        <pre className="ubuntu-banner" ref={bannerRef}>
          {banner.join("\n")}
        </pre>
      </div>

      <div className="term-body ubuntu-body" ref={model.scrollRef}>
        {lines.map((line, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: scrollback is append-only
          <div key={i} className={lineClass(line)} style={lineStyle(line)}>
            {line.kind === "echo" ? <span className="prompt">{prompt} </span> : null}
            {line.text}
          </div>
        ))}
        {busy ? <div className="line dim">…</div> : null}
        <div className="term-input ubuntu-input">
          {/* A pending question replaces the prompt it interrupts, so what the
              terminal wants is where the caret already is. */}
          <span className="prompt">{model.pending ? model.pending.label : prompt}</span>
          <input
            ref={model.inputRef}
            value={model.input}
            onChange={(e) => model.setInput(e.target.value)}
            onKeyDown={model.onKeyDown}
            // M2/E1: never `disabled`. Disabling a focused element blurs it, and
            // re-enabling does not give focus back — every command that reached
            // the API would drop the caret. `submit` already refuses to run twice.
            aria-busy={busy}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            aria-label="terminal input"
            // biome-ignore lint/a11y/noAutofocus: it is a terminal; the prompt is the page
            autoFocus
          />
        </div>
      </div>
    </div>
  );
}
